// validationOverride — an admin-configured "I know, let it through" checkbox
// that suspends named validations on ONE field.
//
// WHY THIS EXISTS
// A cross-field cap is right almost always and wrong occasionally. A submission
// rate may not exceed the job's client rate — except when the account manager
// has actually agreed a higher one, and the recruiter in front of the record
// knows that and the config does not. Before this, the only ways out were to
// weaken the rule for everyone or to edit the job. Both lose the signal.
//
// So the rule STAYS, and the exception becomes explicit, deliberate and
// recorded: a checkbox beside the field's label, a confirmation that says
// plainly what is being given up, and a stored flag on the record saying an
// override was taken.
//
// Entirely config-driven (FormGroupField.overrideCheckbox). Nothing here names
// a module, a field or a rate — any field may gate any of its own validation
// types this way.
//
//   overrideCheckbox: {
//     label:           "Rate agreed above the job's client rate",
//     field:           "candidateRateOverride",   // where the flag is stored
//     skipValidations: ["maxFieldWithFallback"],  // which rule TYPES it lifts
//     confirmTitle:    "…", confirmBody: "…",
//     confirmOkText:   "…", confirmCancelText: "…",
//     store:           true,                      // persist the flag (default)
//   }

const truthy = (v) => v === true || v === 1 || v === '1' || v === 'true';

/** The config block, or null when this field has no override checkbox. */
export function overrideConfig(field) {
  const cfg = field?.overrideCheckbox;
  if (!cfg || typeof cfg !== 'object') return null;
  // A checkbox that lifts nothing is not a checkbox — it would render a control
  // that visibly does nothing, which is worse than not rendering it.
  return overrideSkips(field).size > 0 ? cfg : null;
}

/**
 * The form/payload key holding the flag.
 *
 * Defaults to a deterministic key derived from the field's own key, so an admin
 * who only ticks "add an override checkbox" gets a working, collision-free flag
 * without having to invent a name. Dots become underscores: the flag is a flat
 * sibling, not a leaf inside the field's own container.
 */
export function overrideFieldKey(field) {
  const explicit = String(field?.overrideCheckbox?.field ?? '').trim();
  if (explicit) return explicit;
  return `${String(field?.field ?? '').replace(/\./g, '_')}__override`;
}

/** Validation TYPES this checkbox suspends, as a Set. */
export function overrideSkips(field) {
  const raw = field?.overrideCheckbox?.skipValidations;
  const list = Array.isArray(raw)
    ? raw
    : String(raw ?? '').split(',');
  return new Set(list.map((t) => String(t ?? '').trim()).filter(Boolean));
}

/** Should the flag be written to the record? Defaults to YES — it is an audit fact. */
export function overrideIsStored(field) {
  return field?.overrideCheckbox?.store !== false;
}

/**
 * The absolute name path of the flag, scoped exactly like a cross-field rule's
 * referenced key: the same addRow row when the field sits in one, top level
 * otherwise. So a per-row override stays that row's own.
 */
export function overrideFlagPath(field, name, prefixFor) {
  const key = overrideFieldKey(field);
  // A row's own override, when the field sits in a repeatable group — ticking
  // it on one row must not lift the limit on every other row.
  if (typeof prefixFor === 'function') {
    const prefix = prefixFor(key);
    if (Array.isArray(prefix) && prefix.length) return [...prefix, key];
  }
  // prefixFor answers for the GROUP'S OWN field keys, and the flag is not one
  // of them, so it declines (null) even inside a row. Recover the row from the
  // field's absolute path instead: inside a Form.List it is
  // [listName, rowIndex, …].
  if (Array.isArray(name) && name.length >= 3 && typeof name[1] === 'number') {
    return [name[0], name[1], key];
  }
  // Deliberately NOT resolveScopedFieldPath's sibling fallback: for a dotted
  // field ("candidateBudget.candidateBudgetStart") that would bury the flag
  // INSIDE the field's own container — which, for an appendHistory container,
  // means inside each pushed history entry rather than on the record. The flag
  // is a flat sibling at the field's scope root, which is also exactly where
  // the forms register its hidden Form.Item.
  return [key];
}

/** Is the override currently taken, per live form state? */
export function overrideActive(form, field, name, prefixFor) {
  if (!overrideConfig(field)) return false;
  try {
    return truthy(form?.getFieldValue?.(overrideFlagPath(field, name, prefixFor)));
  } catch {
    return false;
  }
}

/**
 * gateRuleByOverride — wrap ONE built rule so it stands down while the override
 * is taken.
 *
 * Returned as antd's FUNCTION form of a rule, which antd re-evaluates on every
 * validation pass against the CURRENT form. That is what makes the suspension
 * live: ticking the box clears the error without re-rendering the rule list,
 * and unticking it brings the rule straight back. An object rule captured at
 * render time could not do either.
 *
 * Works for declarative rules ({pattern}, {max}, …) and validator rules alike —
 * neither is inspected, only returned or withheld.
 */
export function gateRuleByOverride(builtRule, flagPath) {
  return (formInstance) => {
    let active = false;
    try {
      active = truthy(formInstance?.getFieldValue?.(flagPath));
    } catch {
      active = false;
    }
    // `{}` is antd's no-op rule: it validates nothing and never rejects.
    return active ? {} : builtRule;
  };
}

/**
 * applyOverrideGating — the single call site each form's getRules makes.
 *
 * Takes the RAW admin validations (which still carry their `type`) and returns
 * a mapper that gates only the types this field's checkbox names, leaving every
 * other rule exactly as it was.
 */
export function overrideRuleMapper(field, name, prefixFor, buildRule) {
  const cfg = overrideConfig(field);
  if (!cfg) return buildRule;
  const skips = overrideSkips(field);
  const flagPath = overrideFlagPath(field, name, prefixFor);
  return (validation) => {
    const built = buildRule(validation);
    const type = typeof validation === 'string' ? validation : validation?.type;
    return skips.has(type) ? gateRuleByOverride(built, flagPath) : built;
  };
}

/** Dialog copy, with defaults that read as a deliberate, reversible decision. */
export function overrideConfirmCopy(field) {
  const cfg = overrideConfig(field) ?? {};
  const label = field?.label ?? field?.field ?? 'this field';
  return {
    tone: cfg.tone ?? 'overwrite',
    title: cfg.confirmTitle || 'Remove the validation on this field?',
    body: cfg.confirmBody
      || `The check that keeps ${label} within its allowed limit will be turned off for this record, `
      + 'and a value above that limit will be accepted. The override is recorded against the record. '
      + 'Do you want to continue?',
    okText: cfg.confirmOkText || 'Yes, remove the limit',
    cancelText: cfg.confirmCancelText || 'Keep the limit',
  };
}

export default {
  overrideConfig,
  overrideFieldKey,
  overrideSkips,
  overrideIsStored,
  overrideFlagPath,
  overrideActive,
  gateRuleByOverride,
  overrideRuleMapper,
  overrideConfirmCopy,
};
